import { useCallback } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { Api } from 'src/models/Api.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

import { emailPattern } from 'src/utils/validationRules';

import LoginContainer from './LoginContainer';

const ForgotPassword = () => {
  const history = useHistory();

  const { tPages, tCommon, language } = useUiTranslation();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: '',
    },
  });

  const handleSendResetCode = useCallback(
    async (email: string) => {
      try {
        await Api.remote.sendResetCode(email);
        history.push(`/reset-password-with-otp/${email}`);
      } catch (err) {
        const errorMessage = handleHttpRequestError(err);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: errorMessage,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
      }
    },
    [handleHttpRequestError, history, language, tCommon],
  );

  const onSubmit: SubmitHandler<{
    email: string;
  }> = (data) => {
    handleSendResetCode(data.email);
  };

  return (
    <LoginContainer>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>
              <strong>{tPages('loginLogout.fields.registeredEmail')}</strong>
            </Form.Label>
            <Form.Control
              autoFocus
              id="email"
              type="text"
              placeholder={tPages('loginLogout.fields.emailPlaceholder')}
              autoComplete="off"
              {...register('email', {
                required: tPages('loginLogout.fields.emailRequired'),
                pattern: {
                  value: emailPattern,
                  message: tPages('loginLogout.fields.emailInvalid'),
                },
                maxLength: {
                  value: 254,
                  message: tPages('loginLogout.fields.emailMaxLength'),
                },
              })}
            ></Form.Control>

            {errors.email?.message && (
              <p className="text-danger">{errors.email?.message}</p>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary-100"
            onClick={() => {
              history.goBack();
            }}
          >
            {tPages('changePassword.back')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting}
            data-test="connect-camera"
          >
            {isSubmitting
              ? tPages('changePassword.submitting')
              : tPages('changePassword.continue')}
          </Button>
        </Modal.Footer>
      </Form>
    </LoginContainer>
  );
};

export default ForgotPassword;
